/* eslint-disable react-hooks/exhaustive-deps */
import { FilterOutlined } from '@ant-design/icons'
import { Button, Empty, Input, Spin, Table, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import {
  ArrowBigDownDash,
  ArrowBigUpDash,
  ArrowDownUp,
  ArrowRightLeft,
  Check,
  CheckCheck,
  Eye,
  Hourglass,
  Pencil,
  X,
} from 'lucide-react'
import moment from 'moment'
import { ChangeEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useGeneral, useUser } from '@contexts'
import { useDestinations, useSuppliers } from '@hooks'
import { RoleEnum } from '@types'

import StatusMonitor from '../../../components/StatusMonitor'
import {
  ActionsCellContainer,
  ContentContainer,
  FlexCol,
  FlexRow,
  StyledRow,
  StyledSelect,
} from '../../../components/StyledComponents'
import { DATE_KEYS } from '../../../constants/dateKeys'
import { useFiltersV2 } from '../../../contexts/FiltersContextV2'
import useBlocktrains from '../../../hooks/blocktrain/useBlocktrains'
import useBlocktrainStatusCount from '../../../hooks/blocktrain/useBlocktrainStatusCount'
import useInputFilter from '../../../hooks/useInputFilter'
import { theme } from '../../../styles'
import { Blocktrain, BlockTrainStatusEnum, BlocktrainType, TrainCompany } from '../../../types'
import debounce from '../../../utils/debounce'
import ChangeBlocktrainStatusModal from './ChangeStatusModal'
import CancelBlocktrainModal from './CancelBlocktrainModal'

const { AUTHORIZED, FINISHED, LOADING, TRANSIT, UNLOADING, WAITING_APPROVAL, CANCELED } =
  BlockTrainStatusEnum

const iconByStatus = {
  [LOADING]: <ArrowBigUpDash className="icon-row" />,
  [WAITING_APPROVAL]: <Hourglass className="icon-row" />,
  [AUTHORIZED]: <Check className="icon-row" />,
  [TRANSIT]: <ArrowDownUp className="icon-row" />,
  [UNLOADING]: <ArrowBigDownDash className="icon-row" />,
  [FINISHED]: <CheckCheck className="icon-row" />,
  [CANCELED]: <X className="icon-row" />,
}

const statusToMonitor: Record<string, { labelColor?: string }> = {
  [LOADING]: {},
  [WAITING_APPROVAL]: {
    labelColor: theme.red,
  },
  [AUTHORIZED]: {},
  [TRANSIT]: {},
  [UNLOADING]: {},
}

const BlocktrainsAdminView = () => {
  const { t } = useTranslation()
  const { filters, handleFilterChange, handleResetFilters } = useFiltersV2()
  const { blocktrains, blocktrainsIsLoading } = useBlocktrains({ filters })
  const navigate = useNavigate()
  const { suppliersOptions, isLoading: isSuppliersLoading } = useSuppliers()
  const { destinationsOptions, isLoading: isDestinationsLoading } = useDestinations()
  const { blocktrainStatusCount } = useBlocktrainStatusCount()
  const { state: user } = useUser()

  const [modalVisible, setModalVisible] = useState(false)
  const [selectedBlocktrain, setSelectedBlocktrain] = useState<BlocktrainType | null>(null)
  const [cancelModalVisible, setCancelModalVisible] = useState(false)
  const [selectedBlocktrainForCancel, setSelectedBlocktrainForCancel] =
    useState<BlocktrainType | null>(null)

  const { setTicketToSearch, filtersDisplayed, handleDisplayFilters, menuDisplayed } = useGeneral()

  const handleOpenStatusChange = (record: BlocktrainType) => {
    setSelectedBlocktrain(record)
    setModalVisible(true)
  }

  const actionButtons = (record: BlocktrainType) => {
    const handleClick = () => {
      navigate(record.id.toString())
    }

    const handleCancel = () => {
      setSelectedBlocktrainForCancel(record)
      setCancelModalVisible(true)
    }

    const isAdminRO = user?.roles.some(role => role.name === RoleEnum.ADMINRO)
    const isAdmin = user?.roles.some(role => role.name === RoleEnum.ADMIN)
    const canCancel = isAdmin && record.status !== CANCELED

    const shouldShowPencil =
      record.status === FINISHED || (!isAdminRO && !isAdmin && record.status === UNLOADING)

    return (
      <ActionsCellContainer
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isAdminRO ? 'center' : 'space-between',
          width: '100%',
          flexWrap: 'nowrap',
        }}
      >
        <Button className="icon-button" onClick={handleClick}>
          {shouldShowPencil ? <Pencil /> : <Eye />}
        </Button>

        {!isAdminRO && (
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            {record.status !== LOADING && (
              <Button
                className="icon-button"
                type="primary"
                onClick={() => handleOpenStatusChange(record)}
              >
                <ArrowRightLeft />
              </Button>
            )}
          </div>
        )}

        {canCancel && !isAdminRO && (
          <Tooltip title={t('cancel_blocktrain')} placement="top">
            <Button
              className="icon-button icon-button-right"
              type="primary"
              danger
              onClick={handleCancel}
            >
              <X />
            </Button>
          </Tooltip>
        )}
      </ActionsCellContainer>
    )
  }

  const orderByOptions = [
    {
      label: `${t('order_by_ID')} ${t('order_by_ascending')}`,
      value: { field: 'id', direction: 'ASC' },
    },
    {
      label: `${t('order_by_ID')} ${t('order_by_descending')}`,
      value: { field: 'id', direction: 'DESC' },
    },
  ]

  const rowClassName = (_record: unknown, index: number) =>
    index % 2 === 0 ? 'zebra-even' : 'zebra-odd'

  const columnsBlocktrains: ColumnsType<Blocktrain> = [
    {
      title: t('train_ID'),
      dataIndex: 'id',
      key: 'trainId',
      render: (id: string) => id ?? '-',
    },
    {
      title: t('supplier'),
      dataIndex: 'supplier',
      key: 'supplier',
      render: (supplier: { tradeName: string }) => supplier?.tradeName ?? '-',
    },
    {
      title: t('origin'),
      dataIndex: 'origin',
      key: 'originCity',
      render: (origin: { city: string | null }) => origin?.city ?? '-',
    },
    {
      title: t('destination'),
      dataIndex: 'destination',
      key: 'destinationCity',
      render: (destination: { tag: string | null }) => destination?.tag ?? '-',
    },
    {
      title: t('railcars_#'),
      dataIndex: 'railCarQuantity',
      key: 'railcarsNumber',
      render: (railCarQuantity: string) => railCarQuantity ?? '-',
    },
    {
      title: t('railcars_CO'),
      dataIndex: 'trainCompany',
      key: 'railcarCo',
      render: (trainCompany: TrainCompany) => trainCompany?.shortName ?? '-',
    },
    {
      title: t('ETD'),
      dataIndex: 'estimatedDateOfDeparture',
      key: 'etd',
      render: (estimatedDateOfDeparture: string) =>
        estimatedDateOfDeparture
          ? moment(estimatedDateOfDeparture).format(DATE_KEYS.FORMAT.YEAR_MONTH)
          : '-',
    },
    {
      title: t('railway_bill_#'),
      dataIndex: 'railCarWayBill',
      key: 'railCarWayBill',
      render: (sap_id: string) => sap_id ?? '-',
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'statusname',
      render: (status: BlockTrainStatusEnum) => (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {iconByStatus[status]}
          {t(`blocktrain_status.${status}`)}
        </span>
      ),
    },
    {
      title: t('actions'),
      key: 'acciones',
      align: 'center',
      className: 'column-width',
      render: actionButtons,
    },
  ]

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterOptions = (input: string, option: any) => {
    if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
    return true
  }

  const handleCleanSearchInput = () => {
    setTicketToSearch(undefined)
  }
  const handleRailcarWayBill = debounce((e: ChangeEvent<HTMLInputElement>) => {
    handleFilterChange('railCarWayBill')(e.target.value)
  }, 500)

  useInputFilter(['railCarWayBillElement'])

  return (
    <ContentContainer className="admin-dashboard">
      <FlexCol>
        <FlexRow className="blocktrain-view__header">
          <FlexRow className="blocktrain-view__header__monitor-wrapper">
            {Object.entries(statusToMonitor).map(([key, cfg]) => (
              <StatusMonitor
                key={key}
                label={t(`blocktrain_status.${key}`)}
                value={blocktrainStatusCount?.[key as BlockTrainStatusEnum]}
                labelColor={cfg?.labelColor}
                onClick={() => handleFilterChange('statusName')([key])}
                isActive={
                  Array.isArray(filters?.statusName) &&
                  (filters?.statusName as string[]).includes(key)
                }
              />
            ))}
            <Tooltip placement="bottomRight" title={t('clean_filters')}>
              <Button
                onClick={handleResetFilters}
                style={{ backgroundColor: 'unset', marginLeft: 'auto' }}
              >
                {t('clean_filters')}
              </Button>
            </Tooltip>
          </FlexRow>
        </FlexRow>
        <StyledRow className="sticky-options">
          <div
            className={`filters-container${
              filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
            }`}
          >
            <Button onClick={handleDisplayFilters} id="displayFiltersButton">
              <FilterOutlined />
            </Button>
            <div className="filters-list">
              <StyledSelect
                mode="multiple"
                allowClear
                placeholder={t('filter_by_status')}
                options={Object.values(BlockTrainStatusEnum)
                  .filter(status => status !== 'FINISHED' && status !== 'CANCELED')
                  .map(status => ({
                    label: t(`blocktrain_status.${status}`),
                    value: status,
                  }))}
                onChange={handleFilterChange('statusName')}
                value={filters?.statusName}
                onDropdownVisibleChange={handleCleanSearchInput}
                maxTagCount="responsive"
              />
              <StyledSelect
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                placeholder={t('filter_by_supplier')}
                options={suppliersOptions}
                filterOption={handleFilterOptions}
                onChange={handleFilterChange('supplierId')}
                loading={isSuppliersLoading}
                value={filters?.supplierId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                placeholder={t('filter_by_destination')}
                options={destinationsOptions}
                filterOption={handleFilterOptions}
                onChange={handleFilterChange('destinationId')}
                loading={isDestinationsLoading}
                value={filters?.destinationId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                placeholder={t('order_by_ID')}
                options={orderByOptions.map(option => ({
                  label: option.label,
                  value: JSON.stringify(option.value),
                }))}
                onChange={(value: unknown) => {
                  const stringValue = value as string
                  const selectedValue = JSON.parse(stringValue) as {
                    field: string
                    direction: string
                  }
                  handleFilterChange('orderBy')({ [selectedValue.field]: selectedValue.direction })
                }}
                value={
                  filters?.orderBy
                    ? JSON.stringify({
                        field: Object.keys(filters.orderBy)[0],
                        direction: Object.values(filters.orderBy)[0],
                      })
                    : undefined
                }
                maxTagCount="responsive"
                allowClear
                filterOption={handleFilterOptions}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <div className="search-container">
                <Input
                  style={{ maxWidth: '11rem' }}
                  placeholder={t('filter_by_railwaybill')}
                  onChange={handleRailcarWayBill}
                  allowClear
                  id="railCarWayBillElement"
                />
              </div>
            </div>
          </div>
        </StyledRow>
      </FlexCol>

      {blocktrainsIsLoading ? <Spin /> : null}
      <Table
        columns={columnsBlocktrains}
        dataSource={blocktrains?.blockTrains}
        rowKey="id"
        rowClassName={rowClassName}
        pagination={{
          pageSize: filters?.limit as number,
          onChange: handleFilterChange('page', false),
          total: blocktrains?.total,
          showSizeChanger: false,
          current: filters?.page as number,
        }}
        locale={{
          emptyText: <Empty description={t('no_data_found')} />,
        }}
        scroll={{
          x: 'max-content',
        }}
        virtual
      />
      {selectedBlocktrain && (
        <ChangeBlocktrainStatusModal
          visible={modalVisible}
          close={() => setModalVisible(false)}
          blocktrain={selectedBlocktrain}
        />
      )}
      {selectedBlocktrainForCancel && (
        <CancelBlocktrainModal
          visible={cancelModalVisible}
          close={() => {
            setCancelModalVisible(false)
            setSelectedBlocktrainForCancel(null)
          }}
          blocktrain={selectedBlocktrainForCancel}
        />
      )}
    </ContentContainer>
  )
}

export { BlocktrainsAdminView }
